import { Download, Link2, RefreshCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BookExportReportPanel } from "@/components/book-assembly/book-export-report-panel";
import { Button } from "@/components/ui/button";
import type {
  BookAssemblyRunState,
  BookExportJob,
  RenderedBookExportTarget,
} from "@/features/book-assembly/book-assembly.types";
import {
  canRetryBookExportJob,
  hasBookExportArtifacts,
} from "@/lib/book-assembly/export-report";
import { getBookExportCurrentStageLabelKey, getRunStatusLabelKey } from "@/lib/book-assembly/export-runner";

type BookExportGenerateStepProps = {
  canGenerate: boolean;
  job: BookExportJob | null;
  onCopyArtifactLink: (target: RenderedBookExportTarget) => void;
  onDownloadArtifact: (target: RenderedBookExportTarget) => void;
  onGenerate: () => void;
  onPrepareFreshExport: () => void;
  onRetry: (jobId: string) => void;
  run: BookAssemblyRunState;
  summary: {
    bookTitle: string;
    formatLabel: string;
    itemCount: number;
  };
};

function getJobStatusLabelKey(status: BookExportJob["status"] | null | undefined) {
  switch (status) {
    case "complete":
      return "bookAssembly.generate.jobStatus.complete";
    case "failed":
      return "bookAssembly.generate.jobStatus.failed";
    case "processing":
      return "bookAssembly.generate.jobStatus.processing";
    case "pending":
    default:
      return "bookAssembly.generate.jobStatus.pending";
  }
}

export function BookExportGenerateStep({
  canGenerate,
  job,
  onCopyArtifactLink,
  onDownloadArtifact,
  onGenerate,
  onPrepareFreshExport,
  onRetry,
  run,
  summary,
}: BookExportGenerateStepProps) {
  const { t } = useTranslation();
  const running = job
    ? job.status === "pending" || job.status === "processing"
    : ["validating", "assembling", "rendering", "saving"].includes(run.status);
  const failed = job?.status === "failed" || run.status === "failed";
  const completed = job?.status === "complete" && hasBookExportArtifacts(job);
  const canRetry = canRetryBookExportJob(job);

  return (
    <div className="space-y-6">
      <section className="space-y-6">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              {t("bookAssembly.steps.generate.eyebrow")}
            </p>
            <h2 className="text-3xl text-foreground">
              {t("bookAssembly.steps.generate.title")}
            </h2>
            <p className="max-w-3xl text-sm leading-7 text-muted-foreground">
              {t("bookAssembly.steps.generate.body")}
            </p>
          </div>

          <div className="border-y border-ink/10 py-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {t("bookAssembly.steps.generate.summaryLabel")}
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground">{t("bookAssembly.steps.generate.bookTitle")}</p>
                <p className="font-semibold text-foreground">{summary.bookTitle}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("bookAssembly.steps.generate.format")}</p>
                <p className="font-semibold text-foreground">{summary.formatLabel}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("bookAssembly.steps.generate.itemCount")}</p>
                <p className="font-semibold text-foreground">{summary.itemCount}</p>
              </div>
            </div>
          </div>

          <div
            aria-live="polite"
            className="space-y-4 border-y border-ink/10 py-5"
            data-testid="book-export-job-card"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground" data-testid="book-export-job-status">
                  {job ? t(getJobStatusLabelKey(job.status)) : t(getRunStatusLabelKey(run.status))}
                </p>
                <p className="text-sm text-muted-foreground">
                  {completed
                    ? t("bookAssembly.generate.successBody", {
                        fileName: job.artifacts[0]?.fileName ?? summary.bookTitle,
                      })
                    : failed
                      ? t("bookAssembly.generate.failureBody")
                      : t("bookAssembly.generate.progressBody")}
                </p>
              </div>
              <p className="text-sm font-semibold text-foreground">{run.progressPercent}%</p>
            </div>

            {job ? (
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="border-t border-ink/10 py-3 text-sm sm:border-t-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {t("bookAssembly.generate.jobIdLabel")}
                  </p>
                  <p className="mt-2 break-all font-medium text-foreground">{job.id}</p>
                </div>
                <div className="border-t border-ink/10 py-3 text-sm sm:border-t-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {t("bookAssembly.generate.stageLabel")}
                  </p>
                  <p className="mt-2 font-medium text-foreground">
                    {t(getBookExportCurrentStageLabelKey(job.currentStage))}
                  </p>
                </div>
                <div className="border-t border-ink/10 py-3 text-sm sm:border-t-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {t("bookAssembly.generate.retryCountLabel")}
                  </p>
                  <p className="mt-2 font-medium text-foreground">{job.retryCount}</p>
                </div>
              </div>
            ) : null}

            <div className="h-3 overflow-hidden rounded-full bg-white/70">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${run.progressPercent}%` }}
              />
            </div>
          </div>

          <div className="border-y border-amber-600/20 py-4 text-sm text-amber-950">
            {t("bookAssembly.generate.savedContentNotice")}
          </div>

          {completed ? (
            <div className="space-y-3 border-y border-ink/10 py-5 text-sm text-muted-foreground">
              <div className="space-y-1">
                <p className="font-medium text-foreground">{t("bookAssembly.generate.downloadReady")}</p>
                <p>{t("bookAssembly.generate.downloadHelp")}</p>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                {job.artifacts.map((artifact) => (
                  <div
                    className="border-t border-ink/10 py-4 first:border-t-0"
                    data-testid={`book-export-artifact-${artifact.target}`}
                    key={`${artifact.target}-${artifact.storagePath}`}
                  >
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                      {t(`bookAssembly.summary.targets.${artifact.target}`)}
                    </p>
                    <p className="mt-2 font-semibold text-foreground">{artifact.fileName}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button onClick={() => onDownloadArtifact(artifact.target)} type="button" variant="outline">
                        <Download className="h-4 w-4" />
                        {t("bookAssembly.generate.download")}
                      </Button>
                      <Button onClick={() => onCopyArtifactLink(artifact.target)} type="button" variant="outline">
                        <Link2 className="h-4 w-4" />
                        {t("bookAssembly.generate.copyLink")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <Button disabled={running || !canGenerate} onClick={onGenerate} type="button">
              {completed
                ? t("bookAssembly.generate.generateAgain")
                : t("bookAssembly.generate.generate")}
            </Button>
            {job && !running ? (
              <Button onClick={onPrepareFreshExport} type="button" variant="outline">
                {t("bookAssembly.generate.prepareFreshExport")}
              </Button>
            ) : null}
            {canRetry && job ? (
              <Button onClick={() => onRetry(job.id)} type="button" variant="outline">
                <RefreshCcw className="h-4 w-4" />
                {t("bookAssembly.generate.retry")}
              </Button>
            ) : null}
          </div>
      </section>

      <BookExportReportPanel job={job} />
    </div>
  );
}
